import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/Forms";
import { DIALOG_CLS } from "@/components/Dialogs";
import { FREQ } from "@/lib/tasks";
import { TODAY } from "@/data/mock";

const SUGGESTIONS = [
  [/caixa|recep/i, ["Conferir fundo de caixa na abertura", "Registrar sangrias do dia", "Fechar caixa e informar total de vendas"]],
  [/vend|consult/i, ["Contatar clientes da carteira", "Apresentar lançamentos da semana", "Atualizar quadro de vendas individual"]],
  [/online|digital|site/i, ["Responder mensagens do site e redes", "Atualizar estoque da loja online", "Publicar produtos novos com fotos"]],
  [/estet|cabel|manicure|confeit|costur/i, ["Higienizar e organizar a estação", "Conferir materiais dos atendimentos", "Registrar protocolos realizados"]],
];

const toForm = (t, people) =>
  t
    ? { ...t, assignee: t.collaboratorId ? `c:${t.collaboratorId}` : `r:${t.teamRole}`, processId: t.processId || "none" }
    : { title: "", assignee: `c:${people[0]?.id || ""}`, frequency: "diaria", time: "09:00", dueDate: TODAY, priority: "Média", processId: "none", requiresApproval: false, kind: "administrativo" };

const fromForm = ({ assignee, ...f }) => ({
  ...f,
  collaboratorId: assignee.startsWith("c:") ? assignee.slice(2) : null,
  teamRole: assignee.startsWith("r:") ? assignee.slice(2) : null,
  processId: f.processId === "none" ? null : f.processId,
});

const Pick = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

function TaskForm({ initial, people, processes, onSave }) {
  const [f, setF] = useState(() => toForm(initial, people));
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const roles = [...new Set(people.map((p) => p.role))];
  const assignees = [...people.map((p) => [`c:${p.id}`, `${p.name} · ${p.role}`]), ...roles.map((r) => [`r:${r}`, `Equipe: ${r}`]), ["r:*", "Toda a equipe"]];
  const role = f.assignee.startsWith("c:") ? people.find((p) => `c:${p.id}` === f.assignee)?.role || "" : f.assignee.slice(2);
  const tips = SUGGESTIONS.filter(([re]) => re.test(role)).flatMap(([, list]) => list);
  const submit = (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Descreva a tarefa.");
    if (!f.dueDate) return toast.error("Informe o prazo.");
    onSave(fromForm({ ...f, title: f.title.trim() }));
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="task-form">
      <Field label="Responsável"><Pick value={f.assignee} onChange={set("assignee")} options={assignees} testId="task-assignee-select" /></Field>
      <Field label="Tarefa"><Input value={f.title} onChange={set("title")} placeholder="Ex.: Conferir caixa de abertura" data-testid="task-title-input" /></Field>
      {!!tips.length && (
        <div className="flex flex-wrap gap-1.5" data-testid="task-suggestions">
          {tips.map((t) => <button type="button" key={t} onClick={() => set("title")(t)} className="rounded-full border border-gold/40 bg-gold-soft px-2.5 py-1 text-[11px] text-gold-dark hover:border-gold">{t}</button>)}
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <Field label="Frequência"><Pick value={f.frequency} onChange={set("frequency")} options={Object.entries(FREQ)} testId="task-frequency-select" /></Field>
        <Field label="Prioridade"><Pick value={f.priority} onChange={set("priority")} options={["Alta", "Média", "Baixa"].map((p) => [p, p])} testId="task-priority-select" /></Field>
        <Field label="Horário"><Input type="time" value={f.time} onChange={set("time")} data-testid="task-time-input" /></Field>
        <Field label="Prazo"><Input type="date" value={f.dueDate} onChange={set("dueDate")} data-testid="task-due-input" /></Field>
      </div>
      <Field label="Tipo de atividade"><Pick value={f.kind || "administrativo"} onChange={set("kind")} options={[["operacional", "Operacional · exige foto ao concluir"], ["administrativo", "Administrativa · sem foto"]]} testId="task-kind-select" /></Field>
      <Field label="Processo vinculado"><Pick value={f.processId} onChange={set("processId")} options={[["none", "Nenhum"], ...processes.map((p) => [p.id, p.name])]} testId="task-process-select" /></Field>
      <label className="flex items-center justify-between gap-4 rounded-lg border border-neutral-200 p-3 text-sm">
        <span><span className="font-medium">Precisa de aprovação da empresária</span><span className="block text-xs text-neutral-500">Ao concluir, a tarefa fica aguardando aprovação</span></span>
        <Switch checked={f.requiresApproval} onCheckedChange={set("requiresApproval")} data-testid="task-approval-switch" />
      </label>
      <Button type="submit" className="w-full font-semibold" data-testid="task-save-button">Salvar tarefa</Button>
    </form>
  );
}

export function TaskDialog({ open, onOpenChange, initial, people, processes, onSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={DIALOG_CLS} data-testid="task-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{initial ? "Editar tarefa" : "Nova tarefa"}</DialogTitle></DialogHeader>
        {open && <TaskForm initial={initial} people={people} processes={processes} onSave={onSave} />}
      </DialogContent>
    </Dialog>
  );
}
